/**
 * message-merge.ts — fold delivered `message` rows from multiple fetches (the live "head"
 * page + prepended older-history pages) into ONE de-duplicated, chronologically ordered
 * list for the conversation thread.
 *
 * WHY (chatter-reported 2026-07-13, adversarial-review confirmed): the thread pane used a
 * SINGLE messages query keyed by the paging cursor `['messages', conversationId, cursor]`.
 * Once a chatter clicked "Load older messages" the cursor was set, and every subsequent
 * poll / inbox:delta refetch of that key returned only rows OLDER than the cursor — so a
 * new fan reply re-badged the inbox row but never rendered in the OPEN thread until the
 * chatter sent a message or switched conversations.
 *
 * The fix keeps two independent fetches — a cursor-less HEAD page that always carries the
 * newest rows (the live tail) and cursor-driven OLDER pages that extend history backward —
 * and folds both into the accumulated list through this helper. Merging by id (not concat)
 * is what makes the split correct:
 *   - the live tail keeps flowing no matter how far back the chatter has paged;
 *   - a row present in both a head refresh and an older page appears once (dedup);
 *   - a re-fetched row overwrites its stale copy, so a retranslate / PPV-unlock / delete
 *     flips in place (incoming wins on id collision).
 *
 * Pure and dependency-free (structural generics, no React/DOM/api-client imports) so the
 * ordering is unit-testable without a component harness — mirrors thread-stream.ts.
 */

/** A delivered message: a stable id + the Maloum send time (ISO 8601). */
type Identified = { id: string; sentAt: string };

/** ISO string → epoch ms; a malformed/absent timestamp sorts LAST (never floats to the top). */
function toEpoch(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * Fold `incoming` into `base`, de-duplicating by `id` and returning an ascending
 * (oldest→newest) list.
 *
 * `incoming` wins on collision — it is the fresher server copy, so translation / PPV /
 * delete state updates land. Input order is irrelevant: `base` is the accumulated ascending
 * list and `incoming` arrives newest-first from the API, and the final sort normalises both,
 * so callers need not pre-reverse. Stable at equal timestamps (a `Map` keeps first-seen key
 * order and `Array.prototype.sort` is stable, ES2019+).
 */
export function mergeMessagesById<M extends Identified>(
  base: readonly M[],
  incoming: readonly M[],
): M[] {
  const byId = new Map<string, M>();
  for (const m of base) byId.set(m.id, m);
  // Map.set on an existing key updates the value in place (keeps the key's original
  // position), so an overwritten row keeps base's slot while carrying incoming's fresher
  // fields; genuinely new ids append and the sort settles the final order.
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => toEpoch(a.sentAt) - toEpoch(b.sentAt));
}

/**
 * Fold the cursor-less HEAD page (always the newest ≤N rows) into the accumulated list.
 *
 * Unlike a plain union, the head page is treated as AUTHORITATIVE for its own time window
 * [oldest-head-row .. newest]: a row that was in the accumulated set but is absent from a
 * fresh head fetch was deleted/omitted server-side (a peer chatter's "delete this message",
 * any deleted_at write, a reconciler correction) and is intentionally dropped — a plain
 * id-union would keep it forever. This restores the pre-split "head replaces the newest
 * window" semantics while KEEPING loaded older history (rows strictly older than the head
 * window, which the head page doesn't cover).
 *
 * Gap guard: if NONE of `prev` falls within the head window (the head window is entirely
 * newer than everything loaded — e.g. >N messages surfaced in one burst between refetches),
 * retaining the older rows would leave an un-pageable hole between them and the head. Collapse
 * to the contiguous head page instead; the dropped history stays one "Load older" away and is
 * now correctly reachable by paging back from the head's oldest row.
 */
export function mergeHeadPage<M extends Identified>(
  prev: readonly M[],
  head: readonly M[],
): M[] {
  if (head.length === 0) return prev.slice(); // defensive: an empty head keeps loaded history
  if (headWindowDisjoint(prev, head)) {
    // Total non-overlap → a gap. Collapse to the contiguous head page.
    return head.slice().sort((a, b) => toEpoch(a.sentAt) - toEpoch(b.sentAt));
  }
  let headOldest = Number.POSITIVE_INFINITY;
  for (const m of head) headOldest = Math.min(headOldest, toEpoch(m.sentAt));
  // Older history the head page does NOT cover (strictly older than its window). Rows inside
  // the window come ONLY from `head`, so anything the server dropped from the window disappears.
  const olderHistory = prev.filter((m) => toEpoch(m.sentAt) < headOldest);
  return mergeMessagesById(olderHistory, head);
}

/**
 * True when the head page's window is entirely NEWER than every accumulated row — i.e. no
 * overlap, so a gap exists between the loaded set and the head page. Single source of truth
 * for `mergeHeadPage`'s collapse branch and the pane's hasMore/cursor bookkeeping (which must
 * re-establish the not-paged invariant when a collapse drops loaded history).
 */
export function headWindowDisjoint<M extends Identified>(
  prev: readonly M[],
  head: readonly M[],
): boolean {
  if (prev.length === 0 || head.length === 0) return false;
  let headOldest = Number.POSITIVE_INFINITY;
  for (const m of head) headOldest = Math.min(headOldest, toEpoch(m.sentAt));
  return prev.every((m) => toEpoch(m.sentAt) < headOldest);
}
